import { canAccessCustomerDebt, CUSTOMER_DEBT_PATH } from '@/security/customerDebt.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { GripVertical, LogOut, PanelLeftClose, PanelLeftOpen, Search, X } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from '@/assets/icon.png';
import { useUI } from '../../context/UIContext.jsx';
import { useAuth } from '../../state/auth/AuthContext.jsx';
import menuData from '../../config/menus.js';
import SidebarItem from './SidebarItem.jsx';
import SidebarSection from './SidebarSection.jsx';
import { useTheme } from '../../theme/ThemeContext.jsx';
import { can, canAny, getPermissionCodeForPath } from '../../utils/permissions.js';
import { getModuleLandingRoute } from '../../utils/moduleNavigation.js';
import { getModuleFeatureCode, isTenantFeatureEnabled } from '../../config/tenantFeatures.js';

function canAccessPermission(user, permissionRequirement) {
  if (!permissionRequirement) return true;
  return Array.isArray(permissionRequirement)
    ? canAny(user, permissionRequirement)
    : can(user, permissionRequirement);
}

function filterMenuEntries(entries, user, tenant) {
  return entries
    .map(entry => {
      const requiredPermission = entry.permissions || getPermissionCodeForPath(entry.to);
      if (entry.type === 'section') {
        const items = entry.items.filter(item => (
          isTenantFeatureEnabled(tenant, item)
          && (item.to !== CUSTOMER_DEBT_PATH || canAccessCustomerDebt(user, tenant))
          && canAccessPermission(user, item.permissions || getPermissionCodeForPath(item.to))
        ));
        if (!items.length || !isTenantFeatureEnabled(tenant, entry) || !canAccessPermission(user, requiredPermission)) {
          return null;
        }
        return { ...entry, items };
      }
      return isTenantFeatureEnabled(tenant, entry) && canAccessPermission(user, requiredPermission) ? entry : null;
    })
    .filter(Boolean);
}

const normalizeSearchText = value => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase();

const pathIsActive = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

function filterEntriesByQuery(entries, query) {
  if (!query) return entries;
  return entries.reduce((result, entry) => {
    const groupMatches = normalizeSearchText(entry.label).includes(query);
    if (entry.type !== 'section') {
      return groupMatches ? [...result, entry] : result;
    }
    const items = groupMatches ? entry.items : entry.items.filter(item => normalizeSearchText(item.label).includes(query));
    return items.length ? [...result, { ...entry, items }] : result;
  }, []);
}

function highlightMatch(label, query) {
  if (!query) return label;
  const normalizedLabel = normalizeSearchText(label);
  const start = normalizedLabel.indexOf(query);
  if (start < 0) return label;
  return <>{label.slice(0, start)}<mark className="sidebar-search-match">{label.slice(start, start + query.length)}</mark>{label.slice(start + query.length)}</>;
}

export default function Sidebar({
  collapsed,
  onToggle,
  overlay = false,
  compact = false,
  showLogoutShortcut = false,
}) {
  const {
    activeModule,
    setActiveModule,
    isSectionOpen,
    toggleSection,
    openSection,
    isCompactViewport,
    sidebarExpandedWidth,
    updateLayoutSetting,
  } = useUI();
  const { tenant, sucursalActiva, user, logout } = useAuth();
  const location = useLocation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const resizeStateRef = useRef(null);
  const navRef = useRef(null);
  const activeItemRef = useRef(null);
  const searchTimerRef = useRef(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCompactHeight, setIsCompactHeight] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerHeight < 760;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncCompactHeight = () => {
      setIsCompactHeight(window.innerHeight < 760);
    };

    syncCompactHeight();
    window.addEventListener('resize', syncCompactHeight);
    return () => window.removeEventListener('resize', syncCompactHeight);
  }, []);

  const filteredMenuData = useMemo(() => {
    return Object.entries(menuData).reduce((acc, [moduleKey, entriesList]) => {
      if (!isTenantFeatureEnabled(tenant, { featureCode: getModuleFeatureCode(moduleKey) })) {
        return acc;
      }
      const filteredEntries = filterMenuEntries(entriesList, user, tenant);
      if (filteredEntries.length) {
        acc[moduleKey] = filteredEntries;
      }
      return acc;
    }, {});
  }, [tenant, user]);

  const moduleOptions = useMemo(() => Object.keys(filteredMenuData), [filteredMenuData]);
  const entries = useMemo(() => filteredMenuData[activeModule] || [], [activeModule, filteredMenuData]);
  const visibleEntries = useMemo(
    () => filterEntriesByQuery(entries, searchQuery),
    [entries, searchQuery],
  );

  useEffect(() => {
    if (!filteredMenuData[activeModule]) {
      const fallback = moduleOptions[0] || 'Cargas';
      setActiveModule(fallback);
    }
  }, [activeModule, filteredMenuData, moduleOptions, setActiveModule]);

  const sectionsStatus = useMemo(() => {
    return entries.reduce((acc, entry) => {
      if (entry.type === 'section') {
        const isActive = entry.items.some(item => pathIsActive(location.pathname, item.to));
        acc[entry.label] = isActive;
      }
      return acc;
    }, {});
  }, [entries, location.pathname]);

  useEffect(() => {
    const activeSection = entries.find(entry => entry.type === 'section' && sectionsStatus[entry.label]);
    if (activeSection) openSection(activeModule, activeSection.label);
  }, [activeModule, entries, openSection, sectionsStatus]);

  useEffect(() => {
    if (!searchInput) {
      setSearchQuery('');
      return undefined;
    }
    searchTimerRef.current = window.setTimeout(() => setSearchQuery(normalizeSearchText(searchInput)), 150);
    return () => window.clearTimeout(searchTimerRef.current);
  }, [searchInput]);

  useEffect(() => {
    if (!activeItemRef.current || collapsed) return undefined;
    const timer = window.setTimeout(() => {
      activeItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [activeModule, collapsed, location.pathname, searchQuery, visibleEntries]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePointerMove = event => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) {
        return;
      }

      const delta = event.clientX - resizeState.startX;
      updateLayoutSetting('sidebarExpandedWidth', resizeState.startWidth + delta);
    };

    const handlePointerUp = () => {
      resizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [updateLayoutSetting]);

  const handleModuleChange = event => {
    const moduleName = event.target.value;
    setActiveModule(moduleName);

    const targetRoute = getModuleLandingRoute(
      moduleName,
      filteredMenuData[moduleName] || menuData[moduleName] || [],
    );
    navigate(targetRoute, { replace: false });
  };

  const handleResizeStart = event => {
    if (collapsed || overlay || isCompactViewport) {
      return;
    }

    resizeStateRef.current = {
      startX: event.clientX,
      startWidth: sidebarExpandedWidth,
    };

    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  const asideClassName = clsx(
    'sidebar relative flex h-[100dvh] shrink-0 flex-col overflow-hidden transition-[width,box-shadow] duration-200',
    collapsed ? 'w-[var(--sidebar-w-collapsed)]' : 'w-full',
    compact && 'shadow-none',
    overlay && 'border-r border-[rgba(var(--border),0.65)] shadow-[0_16px_40px_rgba(var(--shadow),0.18)]',
  );
  const sidebarWidth = collapsed ? 'var(--sidebar-w-collapsed)' : `${sidebarExpandedWidth}px`;

  return (
    <aside
      data-collapsed={collapsed ? 'true' : 'false'}
      className={asideClassName}
      style={{ width: sidebarWidth, minWidth: sidebarWidth, maxWidth: sidebarWidth }}
      aria-label="Navegacion principal"
      data-theme={theme}
    >
      <div
        className={clsx(
          'border-b border-[rgba(var(--border),0.4)]',
          collapsed ? 'px-2 py-2' : 'px-2.5 py-2.5',
        )}
      >
        <NavLink
          to="/"
          className={clsx(
            'group flex items-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
            collapsed ? 'justify-center' : 'gap-2',
          )}
          onClick={() => setActiveModule('Cargas')}
          title={collapsed ? 'Sinamon' : undefined}
        >
          <img
            src={logo}
            alt="Sinamon"
            className={clsx(
              'rounded-lg object-contain transition-transform group-hover:scale-105',
              collapsed ? 'h-8 w-8' : 'h-8 w-8',
            )}
          />
          {!collapsed ? (
            <div className="min-w-0 flex-1">
              <span className="block truncate text-[11px] font-bold uppercase tracking-[0.14em] text-[rgb(var(--fg))]">
                Sinamon
              </span>
              <span className="block truncate text-[10px] text-[rgb(var(--muted))]">
                Suite logistica
              </span>
            </div>
          ) : null}
        </NavLink>

        {!collapsed && (tenant?.razon_social || sucursalActiva?.nombre) ? (
          <div
            className={clsx(
              'mt-1.5 flex flex-col gap-0.5 rounded-lg bg-[rgba(var(--subpanel),0.5)] px-2 py-1',
            )}
          >
            {tenant?.razon_social ? (
              <span className="truncate text-[11px] font-semibold text-[rgb(var(--fg))]">
                {tenant.razon_social}
              </span>
            ) : null}
            {sucursalActiva?.nombre ? (
              <span className="truncate text-[10px] text-[rgb(var(--muted))]">
                Operando: {sucursalActiva.nombre}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {!collapsed ? (
        <div className="px-2.5 pb-1.5 pt-1.5">
          <label
            htmlFor={`sidebar-module-${overlay ? 'overlay' : 'rail'}`}
            className="block text-[10px] font-semibold uppercase tracking-[0.24em]"
            style={{ color: 'rgb(var(--muted))' }}
          >
            Modulo
          </label>
          <select
            id={`sidebar-module-${overlay ? 'overlay' : 'rail'}`}
            value={activeModule}
            onChange={handleModuleChange}
            className={clsx(
              'mt-1 w-full rounded-xl px-3 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
              'py-1.5',
            )}
            style={{
              background: 'rgb(var(--subpanel))',
              color: 'rgb(var(--fg))',
              borderColor: 'rgba(var(--border),0.6)',
            }}
          >
            {moduleOptions.map(module => (
              <option key={module} value={module}>
                {module}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="px-2 pb-1 pt-1">
          <span className="sr-only">Modulo activo: {activeModule}</span>
        </div>
      )}

      <nav
        ref={navRef}
        className={clsx('min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-2 [scrollbar-gutter:stable]', collapsed || isCompactHeight ? 'pb-2' : 'pb-3')}
        aria-label="Secciones de navegacion"
      >
        {!collapsed ? (
          <div className="sticky top-0 z-10 -mx-2 bg-[rgb(var(--panel))] px-2 pb-1.5 pt-0.5">
            <label className="sr-only" htmlFor={`sidebar-search-${overlay ? 'overlay' : 'rail'}`}>Buscar opción</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[rgba(var(--muted),0.9)]" aria-hidden="true" />
              <input
                id={`sidebar-search-${overlay ? 'overlay' : 'rail'}`}
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                className="h-8 w-full min-w-0 rounded-lg bg-[rgba(var(--subpanel),0.9)] py-1 pl-8 pr-8 text-[12px]"
                placeholder="Buscar opción…"
                type="search"
              />
              {searchInput ? <button type="button" onClick={() => setSearchInput('')} className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-[rgb(var(--muted))] hover:bg-[rgba(var(--brand),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" aria-label="Limpiar búsqueda" title="Limpiar búsqueda"><X className="h-3.5 w-3.5" /></button> : null}
            </div>
          </div>
        ) : null}
        <div className="flex flex-col gap-[3px]">
          {visibleEntries.map(entry => {
            if (entry.type === 'section') {
              const open = Boolean(searchQuery) || isSectionOpen(activeModule, entry.label);
              const active = sectionsStatus[entry.label];

              return (
                <SidebarSection
                  key={entry.label}
                  iconKey={entry.iconKey}
                  label={entry.label}
                  labelContent={highlightMatch(entry.label, searchQuery)}
                  items={entry.items}
                  isOpen={open}
                  onToggle={() => toggleSection(activeModule, entry.label)}
                  isCollapsed={collapsed}
                  isActive={active}
                  module={activeModule}
                  itemLabelContent={label => highlightMatch(label, searchQuery)}
                  activeItemRef={item => pathIsActive(location.pathname, item.to) ? element => { activeItemRef.current = element; } : undefined}
                />
              );
            }

            return (
              <SidebarItem
                key={entry.to}
                iconKey={entry.iconKey}
                label={entry.label}
                labelContent={highlightMatch(entry.label, searchQuery)}
                to={entry.to}
                isCollapsed={collapsed}
                elementRef={pathIsActive(location.pathname, entry.to) ? element => { activeItemRef.current = element; } : undefined}
              />
            );
          })}
          {searchQuery && !visibleEntries.length ? <p className="px-2 py-5 text-center text-[12px] text-[rgb(var(--muted))]">No se encontraron opciones</p> : null}
        </div>
      </nav>

      <footer
        className={clsx(
          'mt-auto border-t border-[rgba(var(--border),0.35)]',
          collapsed ? 'px-2 py-2' : isCompactHeight ? 'px-2.5 py-2' : 'px-3 py-3',
        )}
      >
        <div className={clsx(collapsed ? 'flex flex-col items-center gap-2' : 'grid grid-cols-2 gap-2')}>
          {showLogoutShortcut ? (
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/login', { replace: true });
              }}
              className={clsx(
                'inline-flex items-center rounded-xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.92)] text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.92)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                collapsed
                  ? 'h-9 w-9 justify-center'
                  : clsx(
                      'w-full justify-center',
                      isCompactHeight ? 'gap-2 px-2.5 py-1.5 text-[12px] font-medium' : 'gap-2 px-3 py-2 text-[13px] font-medium',
                    ),
              )}
              aria-label="Cerrar sesion"
              title="Cerrar sesion"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              {!collapsed ? <span>Cerrar sesion</span> : null}
            </button>
          ) : null}

          {onToggle ? (
            <button
              type="button"
              onClick={onToggle}
              className={clsx(
                'inline-flex items-center rounded-xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.92)] text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.92)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                collapsed
                  ? 'h-9 w-9 justify-center'
                  : clsx(
                      'w-full justify-center',
                      isCompactHeight ? 'gap-2 px-2.5 py-1.5 text-[12px] font-medium' : 'gap-2 px-3 py-2 text-[13px] font-medium',
                    ),
              )}
              aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
              title={collapsed ? 'Expandir' : 'Colapsar'}
            >
              {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
              {!collapsed ? <span>{collapsed ? 'Expandir' : 'Compactar'}</span> : null}
            </button>
          ) : null}
        </div>
      </footer>

      {!collapsed && !overlay && !isCompactViewport ? (
        <button
          type="button"
          className="absolute bottom-0 right-0 top-0 z-10 flex w-3 translate-x-1/2 items-center justify-center text-[rgba(var(--muted),0.85)] transition hover:text-[rgb(var(--brand))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          onPointerDown={handleResizeStart}
          aria-label="Redimensionar sidebar"
          title="Arrastrar para cambiar ancho"
        >
          <span className="pointer-events-none flex h-14 w-2 items-center justify-center rounded-full border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.96)] shadow-[0_8px_18px_rgba(var(--shadow),0.16)]">
            <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </button>
      ) : null}
    </aside>
  );
}
